import { createContext, useContext, ReactNode } from 'react';
import { useHashRouter } from '@/hooks/app/useHashRouter';
import { RouteType } from '@/lib/hashRouter';

interface HashRouterContextType {
  navigate: (route: RouteType) => void;
}

const HashRouterContext = createContext<HashRouterContextType | null>(null);

interface HashRouterProviderProps {
  children: ReactNode;
}

/**
 * Provider component that initializes hash routing
 * Wrap your app with this to enable hash-based navigation
 */
export function HashRouterProvider({ children }: HashRouterProviderProps) {
  const { navigate } = useHashRouter();

  return (
    <HashRouterContext.Provider value={{ navigate }}>
      {children}
    </HashRouterContext.Provider>
  );
}

/**
 * Hook to access the hash router navigate function
 * Must be used within a HashRouterProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useHashNavigate(): (route: RouteType) => void {
  const context = useContext(HashRouterContext);
  if (!context) {
    throw new Error('useHashNavigate must be used within a HashRouterProvider');
  }
  return context.navigate;
}
